import { Fragment } from "react";
import { formatShare } from "#app/widgets/definitions/shared/format-share";
import { z } from "zod";
import {
  Box,
  Icon,
  Separator,
  StatBand,
  Text,
  WidgetEmpty,
  WidgetSkeleton,
  useWidgetInstance,
  type StatBandColumn,
  type StatBandConnector,
  type WidgetModule,
  type WidgetRenderContext,
  type WidgetSettingField,
  type WidgetSettingsGroup,
} from "@salestrak/ui";
import { EntityTag } from "#app/components/entity";
import type { WidgetTag } from "#app/widgets/dashboards/widget-tags";
import type { WidgetSection } from "#app/widgets/dashboards/widget-sections";
import { widgetFiltersSchema } from "#app/widgets/dashboards/widget-filters";
import { useWidgetDrilldown, type WidgetDrilldownActions } from "#app/widgets/dashboards/widget-drilldown";
import { useWindowEmptyTitle } from "#app/widgets/dashboards/scope/use-window-copy";
import { useDashboardScope } from "#app/widgets/dashboards/scope/dashboard-scope-context";
import { WidgetHeaderScopeFilters } from "../shared/widget-header-filters";
import { SECONDS_PER_DAY, formatDwell } from "../shared/format-dwell";
import {
  pipelineConversion,
  useStageConversion,
  type WorkflowConversion,
} from "./use-stage-conversion";

/**
 * `stage-conversion` — each workflow as a strip of its steps in pipeline
 * order, on the library `StatBand` with connectors: the projects in a step now
 * as the figure, and on every boundary the conversion past the step (of the
 * projects that ever reached this step, the share that went on to a later
 * one) with the average time to that later step under it. A caption under
 * the strip reads how long the whole pipeline takes, first step to last.
 * The steps are the workflow's stages, or the subset `steps` names once a
 * workflow is chosen — a stage left out is invisible to the read, so a
 * blocking stage can be skipped and a project that parked there reads as
 * not advancing. One workflow chosen, a workflow board's own, or every
 * workflow the board can reach; pressing a step opens this card's Data
 * drawer on that stage's projects. The header carries the filter chip and the Only-Me toggle, so a
 * rep reads their own pipeline off the team's board. `display: "rate"` folds
 * the strip to ONE figure per workflow — the whole pipeline's yield, first
 * step to last (`pipelineConversion`) — for a small card beside the metrics.
 */
export const stageConversionConfigSchema = z.looseObject({
  workflow: z.string().nullable().default(null),
  /** The chosen workflow's steps, in any order (the strip keeps stage order);
   *  `null` is every non-canceled stage. */
  steps: z.array(z.string()).nullable().default(null),
  display: z.enum(["strip", "rate"]).default("strip"),
  showConversion: z.boolean().default(true),
  showDuration: z.boolean().default(false),
  filters: widgetFiltersSchema.nullish(),
});

export type StageConversionConfig = z.infer<typeof stageConversionConfigSchema>;


function formatDays(seconds: number): string {
  const days = Math.round(seconds / SECONDS_PER_DAY);
  return days < 1 ? "under a day" : `${days} ${days === 1 ? "day" : "days"}`;
}

function WorkflowStrip({
  row,
  loading,
  showConversion,
  showDuration,
  drill,
  instanceId,
  showWorkflow,
}: {
  row: WorkflowConversion;
  loading: boolean;
  showConversion: boolean;
  showDuration: boolean;
  drill: WidgetDrilldownActions | null;
  instanceId: string | null;
  /** Whether the strip names its own workflow — false where the surface around
   *  it already does. */
  showWorkflow: boolean;
}) {
  const columns = row.steps.map<StatBandColumn>((step) => ({
    id: step.id,
    label: step.label,
    value: step.count.toLocaleString(),
    color: step.color ?? undefined,
    muted: step.count === 0,
    // An empty step has no rows to open; it prints as a figure, not a door.
    onSelect:
      drill && instanceId && step.count > 0
        ? () =>
            drill.openData(instanceId, {
              kind: "dimension",
              dimension: "stage",
              ids: [step.id],
              label: step.label,
            })
        : undefined,
  }));

  // Each boundary reads the step from `steps[i]` to `steps[i + 1]`. With
  // both figures on, the rate leads and the time captions it; with one on,
  // that one is the figure. With neither the band keeps its hairlines.
  const connectors: StatBandConnector[] | undefined =
    showConversion || showDuration
      ? row.steps.slice(0, -1).map((step, index) => {
          const next = row.steps[index + 1];
          const rate = step.conversionToNext === null ? "—" : formatShare(step.conversionToNext, 100, { digits: 1 });
          const time = step.durationToNext === undefined ? "—" : formatDwell(step.durationToNext);
          const rateSentence =
            step.conversionToNext === null ? "no rate" : formatShare(step.conversionToNext, 100, { digits: 1 });
          const timeSentence =
            step.durationToNext === undefined ? "" : `, ${formatDays(step.durationToNext)}`;
          return {
            value: showConversion ? rate : time,
            ...(showConversion && showDuration
              ? { caption: time, captionIcon: "clock" as const }
              : {}),
            tooltip: showConversion
              ? showDuration
                ? `Share of Projects that reached ${step.label} and went on to ${next?.label ?? "the next step"} or a later step; average time from ${step.label} to the step reached next`
                : `Share of Projects that reached ${step.label} and went on to ${next?.label ?? "the next step"} or a later step`
              : `Average time from ${step.label} to the step reached next`,
            ariaLabel: `${step.label} to ${next?.label ?? "next"}: ${
              showConversion ? rateSentence : ""
            }${showDuration ? timeSentence : ""}`,
          };
        })
      : undefined;

  const first = row.steps[0];
  const last = row.steps[row.steps.length - 1];
  const showTotal =
    showDuration && row.totalDurationSeconds !== null && first && last && first !== last;

  return (
    <Box direction="column" gap="xs">
      {showWorkflow ? (
        <EntityTag
          entity={{ kind: "workflow", id: row.workflow.workflowId, workflow: row.workflow }}
          display="full"
          interactive={false}
          weight="medium"
        />
      ) : null}
      {/* The strip stands on the compact figure at every card size. A step
          strip runs out of WIDTH, not height — the steps and their digits are
          the workflow's, so a taller card holds the same row of numbers with
          more air under it, and a bigger figure would only spend width the
          column never had. */}
      <StatBand columns={columns} connectors={connectors} size="sm" loading={loading} />
      {showTotal ? (
        <Box direction="row" align="center" gap="2xs" paddingX="sm">
          <Icon name="clock" size="2xs" color="faint" aria-hidden />
          <Text size="xs" variant="muted" truncate>
            {first.label} to {last.label}: avg {formatDays(row.totalDurationSeconds ?? 0)}
          </Text>
        </Box>
      ) : null}
    </Box>
  );
}

/**
 * One workflow's pipeline as a single figure. Alone on the card it stands like
 * a metric — the rate big, the span it covers as the caption; in a stack every
 * row names its workflow beside the rate.
 */
function WorkflowRate({ row, alone, tall }: { row: WorkflowConversion; alone: boolean; tall: boolean }) {
  const rate = pipelineConversion(row.steps);
  const first = row.steps[0];
  const last = row.steps[row.steps.length - 1];
  const span = first && last && first !== last ? `${first.label} to ${last.label}` : "single step";
  const figure = rate === null ? "—" : formatShare(rate, 100, { digits: 1 });
  if (alone) {
    return (
      <Box fill="flex" align="center" justify="center">
        <Box align="center" gap="2xs">
          <Text size={tall ? "4xl" : "2xl"} weight="bold" truncate={1}>
            {figure}
          </Text>
          <Text size="xs" variant="muted" truncate={1}>
            {span}
          </Text>
        </Box>
      </Box>
    );
  }
  return (
    <Box direction="row" align="center" justify="between" gap="sm">
      <Box direction="column" gap="2xs">
        <EntityTag
          entity={{ kind: "workflow", id: row.workflow.workflowId, workflow: row.workflow }}
          display="full"
          interactive={false}
          weight="medium"
        />
        <Text size="xs" variant="muted" truncate={1}>
          {span}
        </Text>
      </Box>
      <Text size="xl" weight="bold" tabular>
        {figure}
      </Text>
    </Box>
  );
}

function StageConversionRender({ h, config }: WidgetRenderContext<StageConversionConfig>) {
  const { data, loading, error } = useStageConversion(
    config.workflow,
    config.steps,
    config.filters,
  );
  const drill = useWidgetDrilldown();
  const instance = useWidgetInstance();
  const emptyTitle = useWindowEmptyTitle("No Projects", config.filters);
  // A strip names its workflow unless the dashboard IS that workflow — there
  // the chip repeats the page. A board that reaches several workflows, a rail
  // or workspace dashboard, and a catalog preview (no scope at all) all keep
  // it: without the name the strip is a row of numbers belonging to nobody.
  const scope = useDashboardScope();
  const scopeWorkflowId = scope?.entity?.type === "workflow" ? scope.entity.id : null;

  if (error) throw error;
  // The count hook holds the previous answer through a re-query, so the strips
  // stand and the band shows placeholders; only the first paint has nothing.
  if (loading && data.length === 0) {
    return <WidgetSkeleton variant={config.display === "rate" ? "stat" : "band"} />;
  }
  if (data.length === 0) return <WidgetEmpty title={emptyTitle} />;

  if (config.display === "rate") {
    return (
      <Box direction="column" fill="flex" gap="sm">
        {data.map((row, index) => (
          <Fragment key={row.workflow.workflowId}>
            {index > 0 ? <Separator /> : null}
            <WorkflowRate row={row} alone={data.length === 1} tall={h >= 3} />
          </Fragment>
        ))}
      </Box>
    );
  }

  return (
    // The strips take the card's middle, not its top: the band is a fixed
    // height whatever the card is, so a taller card's leftover is air and it
    // reads as air on both sides. `grow` + `shrink={false}` and NOT
    // `fill="flex"` — flex-1 bases the column at zero and lets it shrink under
    // its own content, so a stack of workflows taller than the card would have
    // its top half cut off and out of the scroller's reach. Basis-auto keeps
    // the content height as the floor, and only the surplus is split.
    <Box direction="column" gap="sm" grow shrink={false} justify="center">
      {data.map((row, index) => (
        <Fragment key={row.workflow.workflowId}>
          {index > 0 ? <Separator /> : null}
          <WorkflowStrip
            row={row}
            loading={loading}
            showConversion={config.showConversion}
            showDuration={config.showDuration}
            drill={drill}
            instanceId={instance?.instanceId ?? null}
            showWorkflow={row.workflow.workflowId !== scopeWorkflowId}
          />
        </Fragment>
      ))}
    </Box>
  );
}

const stageConversionSettings = (config: StageConversionConfig): WidgetSettingsGroup[] => [
  {
    title: "Source",
    fields: [
      { key: "workflow", kind: "workflowSource", label: "Workflow" },
      // The app's `stageSteps` control lists the effective workflow's stages
      // (the chosen one, else the dashboard's own) as a checklist.
      {
        key: "steps",
        kind: "stageSteps",
        label: "Steps",
        props: { workflowId: config.workflow },
      },
    ],
  },
  {
    title: "Figures",
    fields: [
      {
        key: "display",
        kind: "select",
        label: "Show as",
        icon: "percent",
        options: [
          { value: "strip", label: "Stage strip" },
          { value: "rate", label: "Overall rate" },
        ],
      },
      // The connector figures belong to the strip; one rate has no connectors.
      ...(config.display === "rate"
        ? []
        : [
            {
              key: "showConversion",
              kind: "toggle",
              label: "Conversion rate",
              icon: "arrow-right",
            } satisfies WidgetSettingField,
            { key: "showDuration", kind: "toggle", label: "Time to next step", icon: "clock" } satisfies WidgetSettingField,
          ]),
    ],
  },
  {
    title: "Filters",
    fields: [{ key: "filters", kind: "issueFilters", label: "Filters" }],
  },
];

export const stageConversionDef: WidgetModule<StageConversionConfig> = {
  type: "stage-conversion",
  title: "Stage conversion",
  icon: "arrow-right",
  // Four steps with three connectors want a half-width card; the floor is the
  // single-figure rate's, which sits in a metric-sized cell. The default height
  // is the strip's: the band and air on both sides of it, no more.
  span: { minW: 3, minH: 3, maxW: 12, maxH: 12, defaultW: 12 },
  configSchema: stageConversionConfigSchema,
  settings: stageConversionSettings,
  headerActions: WidgetHeaderScopeFilters,
  // Strip body: grab the card from anywhere on it — a step press still lands
  // because the band's columns are interactive islands.
  bodyDraggable: true,
  catalog: [
    {
      key: "stage-conversion.default",
      label: "Stage conversion",
      description:
        "Each workflow as a strip of its stages: the count in every step, the share of Projects that went on past it and the average time that took. Pick the steps to read a workflow your own way.",
      section: "workflows" satisfies WidgetSection,
      tags: ["projects", "stages", "workflows"] satisfies readonly WidgetTag[],
      defaultConfig: {},
      defaultW: 12,
    },
    {
      key: "stage-conversion.rate",
      label: "Conversion rate",
      description:
        "One figure per workflow: of the Projects that entered the first step, the share that went on to reach the last.",
      section: "workflows" satisfies WidgetSection,
      tags: ["projects", "stages", "workflows"] satisfies readonly WidgetTag[],
      icon: "percent",
      defaultConfig: { display: "rate" },
      locks: ["display"],
      defaultW: 3,
    },
  ],
  render: StageConversionRender,
};
